// components/Admin/AdminLogo.js
import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Image, Video, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import uploadImage from '../../helpers/uploadImage'; 
import { summaryApi } from 'common';
import { authFetch } from '../../helpers/authFetch';

const AdminLogo = () => {
  const [currentLogo, setCurrentLogo] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        fetchCurrentLogo();
    }, []);

    const fetchCurrentLogo = async () => {
        try {
            const response = await authFetch(summaryApi.getLogo.url, {
                method: summaryApi.getLogo.method
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.logo) {
                    setCurrentLogo(data.logo);
                    setPreviewUrl(data.logo.cloudinaryUrl);
                }
            }
        } catch (error) {
            console.error('Error fetching logo:', error);
            toast.error('Failed to load current logo');
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'video/mp4'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select a valid image or MP4 file');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        // Create preview
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        // Upload file to Cloudinary and then save to database
        await uploadLogo(file);
    };

    const uploadLogo = async (file) => {
        setUploading(true);

        try {
            // Step 1: Upload to Cloudinary using your existing function
            console.log('Uploading to Cloudinary...');
            const cloudinaryResult = await uploadImage(file);
            
            console.log('Cloudinary upload successful:', cloudinaryResult);

            // Step 2: Save to database using summaryApi
            const saveResponse = await authFetch(summaryApi.uploadLogo.url, {
                method: summaryApi.uploadLogo.method,
                
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    imageUrl: cloudinaryResult.secure_url,
                    publicId: cloudinaryResult.public_id,
                    format: cloudinaryResult.format,
                    bytes: cloudinaryResult.bytes
                }),
            });

            const saveData = await saveResponse.json();

            if (saveData.success) {
                toast.success('Logo uploaded successfully!');
                setCurrentLogo(saveData.logo);
                setPreviewUrl(saveData.logo.cloudinaryUrl);
                
                // Revoke the object URL to free memory
                if (previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(previewUrl);
                }
            } else {
                toast.error(saveData.message || 'Failed to save logo to database');
                setPreviewUrl('');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Error uploading logo');
            setPreviewUrl('');
        } finally {
            setUploading(false);
        }
    };

    const deleteLogo = async () => {
        if (!window.confirm('Are you sure you want to delete the current logo?')) {
            return;
        }

        try {
            const response = await authFetch(summaryApi.deleteLogo.url, {
                method: summaryApi.deleteLogo.method              
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Logo deleted successfully!');
                setCurrentLogo(null);
                setPreviewUrl('');
            } else {
                toast.error(data.message || 'Failed to delete logo');
            }
        } catch (error) {
            toast.error('Error deleting logo');
            console.error('Delete error:', error);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-bold mb-4">Logo Management</h2>
            
            <div className="space-y-6">
                {/* Current Logo Preview */}
                <div>
                    <h3 className="text-lg font-medium mb-3">Current Logo</h3>
                    {previewUrl ? (
                        <div className="border rounded-lg p-4 bg-gray-50">
                            {currentLogo?.mimetype?.startsWith('video/') ? (
                                <video 
                                    src={previewUrl} 
                                    controls 
                                    className="max-w-xs max-h-32 object-contain mx-auto"
                                    autoPlay
                                    muted
                                    loop
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img 
                                    src={previewUrl} 
                                    alt="Current Logo" 
                                    className="max-w-xs max-h-32 object-contain mx-auto"
                                />
                            )}
                            <div className="mt-3 text-center text-sm text-gray-600">
                                {currentLogo?.originalName} • {currentLogo?.mimetype}
                            </div>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                            <Image className="mx-auto h-12 w-12 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">No logo uploaded</p>
                        </div>
                    )}
                </div>

                {/* Upload Section */}
                <div>
                    <h3 className="text-lg font-medium mb-3">Upload New Logo</h3>
                    <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <input
                                type="file"
                                id="logo-upload"
                                accept="image/*,video/mp4"
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                            />
                            <label
                                htmlFor="logo-upload"
                                className={`cursor-pointer flex flex-col items-center justify-center ${
                                    uploading ? 'opacity-50' : 'hover:bg-gray-50'
                                }`}
                            >
                                {uploading ? (
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                                        <p className="text-sm text-gray-600">Uploading to Cloudinary...</p>
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-600">
                                            Click to upload logo
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Supports: JPG, PNG, GIF, WebP, SVG, MP4 (Max: 10MB)
                                        </p>
                                    </>
                                )}
                            </label>
                        </div>

                        {/* File Requirements */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start">
                                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium">Logo Requirements:</p>
                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                        <li>Images: JPG, PNG, GIF, WebP, SVG</li>
                                        <li>Videos: MP4 format only</li>
                                        <li>Maximum file size: 10MB</li>
                                        <li>Recommended aspect ratio: 1:1 for best results</li>
                                        <li>Files are stored securely in Cloudinary</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Button */}
                {currentLogo && (
                    <div className="flex justify-end">
                        <button
                            onClick={deleteLogo}
                            disabled={uploading}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete Current Logo
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminLogo;